import { format } from 'bytes';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { isMediaItemPayloadType } from 'APP/main/PopupManager/Popups/MediaMessagePopup/MediaMessagePopup.utils';
import { ImagePayload } from 'STORE/Messages/Message/Payload/ImagePayload/ImagePayload';
import { VideoPayload } from 'STORE/Messages/Message/Payload/VideoPayload/VideoPayload';

import { TMediaItemPayloadType, TMediaItemPayload } from '../MediaMessagePopup.types';

interface IItemMediaParams {
  payload: TMediaItemPayload;
  onRemove?(payload: TMediaItemPayload): void;
}

export interface IItemMediaPresenter {
  payload: TMediaItemPayload | null;
  payloadType: TMediaItemPayloadType | null;
  previewUrl: string | undefined;
  size: string;
  name: string;
  canBeRemoved: boolean;
  setPayload(payload: TMediaItemPayload): void;
  setCanBeRemoved(canBeRemoved: boolean): void;
  onRemove(): void;
}

export const useItemMediaPresenter = (data: IItemMediaParams): IItemMediaPresenter => {
  const { payload, onRemove } = data;

  const presenter = useLocalObservable<IItemMediaPresenter>(() => ({
    payload: null,
    canBeRemoved: false,

    get payloadType(): TMediaItemPayloadType | null {
      const mediaItemPayloadType = isMediaItemPayloadType(presenter.payload?.payloadType)
        ? presenter.payload?.payloadType
        : null;

      return mediaItemPayloadType;
    },

    get previewUrl(): string {
      if (presenter.payload instanceof ImagePayload || presenter.payload instanceof VideoPayload) {
        return presenter.payload.localPreview || presenter.payload.previewUrl;
      }

      return '';
    },

    get size(): string {
      return format(presenter.payload?.fileSize || 0, { unitSeparator: ' ' }) || '';
    },

    get name(): string {
      return presenter.payload?.fileName || '';
    },

    setPayload(payload: TMediaItemPayload): void {
      presenter.payload = payload;
    },

    setCanBeRemoved(canBeRemoved: boolean): void {
      presenter.canBeRemoved = canBeRemoved;
    },

    onRemove(): void {
      if (!presenter.payload) {
        return;
      }
      onRemove?.(presenter.payload);
    },
  }));

  useEffect(() => {
    presenter.setPayload(payload);
    presenter.setCanBeRemoved(Boolean(onRemove));
  }, [payload, onRemove]);

  return presenter;
};
